import { useState } from 'react';
import type { ExecutiveSummary } from '@ace-tracker/shared';
import { METRIC_INFO } from '../../metricInfo';
import { Button } from '../Button';

const LOW_COVERAGE_PERCENT = 85;

/** Reporting coverage as a chip, so leaders see how complete the figures are before reading them. */
export function CoverageChip({ summary }: { summary: ExecutiveSummary }) {
  const percent = Math.round(summary.coverage.coveragePercent);
  const ok = percent >= LOW_COVERAGE_PERCENT;
  return (
    <span
      title={METRIC_INFO.reportingCoverage}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill px-3 py-1 text-xs font-medium ${
        ok ? 'bg-ok-bg text-ok-ink' : 'bg-warn-bg text-warn-ink'
      }`}
    >
      <i aria-hidden="true" className={`fa-solid ${ok ? 'fa-circle-check' : 'fa-triangle-exclamation'}`} />
      {percent}% reporting coverage
    </span>
  );
}

/** Downloads the current view as a single editable PowerPoint slide. */
export function DownloadSlideButton({ summary }: { summary?: ExecutiveSummary }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function handleClick() {
    if (!summary) return;
    setBusy(true);
    setError(undefined);
    try {
      const { downloadExecutiveSlide } = await import('./exportSlide');
      await downloadExecutiveSlide(summary);
    } catch (err) {
      setError(`Couldn't build the slide: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Button type="button" className="!px-6 !py-2.5 !text-sm" onClick={() => void handleClick()} disabled={!summary || busy} aria-busy={busy}>
        <i aria-hidden="true" className={`fa-solid ${busy ? 'fa-spinner animate-spin' : 'fa-download'}`} />
        {busy ? 'Building slide…' : 'Download slide'}
      </Button>
      <span className="max-w-[16rem] text-xs font-medium text-error" aria-live="polite">
        {error}
      </span>
    </div>
  );
}
